import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  CircleX,
  Clock3,
  FileSearch,
  LoaderCircle,
  RotateCcw,
  Square,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface Job {
  id: string;
  type: string;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress: number;
  input: Record<string, unknown>;
  output: Record<string, unknown> | null;
  errorMessage: string | null;
  attempts: number;
  maxAttempts: number;
  createdAt: string;
  completedAt: string | null;
  events: Array<{ id: string; messageAr: string; progress: number; createdAt: string }>;
}

const labels: Record<Job['status'], string> = {
  QUEUED: 'في الانتظار',
  RUNNING: 'جارٍ التنفيذ',
  COMPLETED: 'مكتملة',
  FAILED: 'فاشلة',
  CANCELLED: 'ملغاة',
};

const statusStyle: Record<Job['status'], string> = {
  QUEUED: 'bg-amber-50 text-amber-700 border-amber-200',
  RUNNING: 'bg-blue-50 text-blue-700 border-blue-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  FAILED: 'bg-red-50 text-red-700 border-red-200',
  CANCELLED: 'bg-slate-100 text-slate-600 border-slate-200',
};

export function JobsPage() {
  const queryClient = useQueryClient();
  const jobsQuery = useQuery({
    queryKey: ['jobs'],
    queryFn: () => api<Job[]>('/api/jobs'),
    refetchInterval: 3_000,
  });

  const retry = useMutation({
    mutationFn: (id: string) => api(`/api/jobs/${id}/retry`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('أُعيدت المهمة إلى الطابور');
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error) => toast.error(error.message),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/api/jobs/${id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('أُلغيت المهمة');
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (error) => toast.error(error.message),
  });

  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-black text-teal">المعالجة المحلية</p>
            <h1 className="mt-2 text-2xl font-black">المهام والطابور</h1>
            <p className="mt-2 text-xs font-semibold text-slate-500">
              تابع المعالجة، التقدم، المحاولات، وحذف الملفات المؤقتة.
            </p>
          </div>
          <div className="rounded-xl border border-ink/10 bg-white px-4 py-3 text-xs font-bold text-slate-500">
            تحديث تلقائي كل 3 ثوانٍ
          </div>
        </header>

        {jobsQuery.isLoading ? (
          <div className="grid min-h-64 place-items-center">
            <LoaderCircle className="h-6 w-6 animate-spin text-teal" />
          </div>
        ) : jobsQuery.data?.length ? (
          <div className="space-y-3">
            {jobsQuery.data.map((job) => (
              <article key={job.id} className="rounded-2xl border border-ink/10 bg-white/80 p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
                      {job.status === 'COMPLETED' ? (
                        <CheckCircle2 className="h-5 w-5" />
                      ) : job.status === 'FAILED' ? (
                        <CircleX className="h-5 w-5 text-red-200" />
                      ) : job.status === 'RUNNING' ? (
                        <LoaderCircle className="h-5 w-5 animate-spin" />
                      ) : (
                        <Clock3 className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-sm font-black">
                          {job.type === 'PROCESS_ATTACHMENT' ? 'معالجة ملف' : job.type}
                        </h2>
                        <span className={`rounded-full border px-2 py-0.5 text-[9px] font-black ${statusStyle[job.status]}`}>
                          {labels[job.status]}
                        </span>
                      </div>
                      <p className="mt-1 text-[10px] font-bold text-slate-400">
                        {new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(job.createdAt))}
                        {' · '}المحاولة {job.attempts}/{job.maxAttempts}
                      </p>
                      {job.events[0]?.messageAr && (
                        <p className="mt-2 text-xs font-semibold text-slate-600">{job.events[0].messageAr}</p>
                      )}
                      {job.errorMessage && (
                        <p className="mt-2 text-[10px] font-bold text-red-600">{job.errorMessage}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {['QUEUED', 'RUNNING'].includes(job.status) && (
                      <button
                        type="button"
                        onClick={() => cancel.mutate(job.id)}
                        disabled={cancel.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-ink/10 px-3 py-2 text-[10px] font-black text-slate-600 hover:border-red-200 hover:text-red-600"
                      >
                        <Square className="h-3 w-3" /> إلغاء
                      </button>
                    )}
                    {['FAILED', 'CANCELLED'].includes(job.status) && (
                      <button
                        type="button"
                        onClick={() => retry.mutate(job.id)}
                        disabled={retry.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-ink/10 px-3 py-2 text-[10px] font-black text-slate-600 hover:border-teal/30 hover:text-teal"
                      >
                        <RotateCcw className="h-3 w-3" /> إعادة
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full transition-all ${
                      job.status === 'FAILED' ? 'bg-red-400' : 'bg-teal'
                    }`}
                    style={{ width: `${job.progress}%` }}
                  />
                </div>
                {job.output && (
                  <>
                    {typeof job.output.outputAttachmentId === 'string' && (
                      <a
                        href={`/api/files/${job.output.outputAttachmentId}`}
                        className="mt-3 inline-block rounded-lg bg-teal/10 px-3 py-2 text-[10px] font-black text-teal hover:bg-teal/20"
                      >
                        تنزيل الملف الناتج
                      </a>
                    )}
                    {typeof job.output.verificationUrl === 'string' && (
                      <a
                        href={job.output.verificationUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ms-2 mt-3 inline-block rounded-lg bg-gold/10 px-3 py-2 text-[10px] font-black text-gold hover:bg-gold/20"
                      >
                        سجل التحقق
                      </a>
                    )}
                    <pre className="mt-3 max-h-28 overflow-auto rounded-xl bg-slate-50 p-3 text-[10px] text-slate-500" dir="ltr">
                      {JSON.stringify(job.output, null, 2)}
                    </pre>
                  </>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-ink/15 bg-white/50 px-6 py-20 text-center">
            <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-2xl bg-teal/10 text-teal">
              <FileSearch className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-black">لا توجد مهام بعد</h2>
            <p className="mx-auto mt-2 max-w-md text-xs font-semibold leading-6 text-slate-500">
              ارفع ملفًا داخل المساعد ثم شغّل المعالجة المحلية لتظهر حالته هنا.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
